import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@rossum/ui/material';
import { closeModal } from '../../redux/modules/modal/actions';
import { State as ReduxState } from '../../types/state';
import { ConfirmTypeT, ModalKey } from './types';

type DispatchProps = {
  close: () => void;
  dispatch: Dispatch;
};

type StateProps = {
  confirmAction?: () => AnyAction;
  confirmType?: ConfirmTypeT;
  isOpen?: boolean;
  onConfirm?: () => void;
  textId?: ModalKey;
  values?: Record<string, unknown>;
};

type OwnProps = {
  children?: ReactNode;
  isOpen?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  textId?: ModalKey;
  values?: Record<string, unknown>;
  confirmType?: ConfirmTypeT;
};

type Props = StateProps & OwnProps & DispatchProps;
const ReduxModal = ({
  children,
  close,
  confirmAction,
  confirmType,
  dispatch,
  isOpen,
  onCancel,
  onConfirm,
  textId,
  values,
}: Props) => {
  const intl = useIntl();

  if (!textId) {
    return null;
  }

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    if (confirmAction) dispatch(confirmAction());
    close();
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    if (close) close();
  };

  return (
    <Dialog
      open={isOpen || false}
      onClose={() => {
        handleCancel();
      }}
      slotProps={{ paper: { sx: { minWidth: 400 }, elevation: 2 } }}
    >
      <DialogTitle
        title={intl.formatMessage({
          id: `components.modal.${textId}.title`,
        })}
        onClose={() => handleCancel()}
      />
      <DialogContent>
        {!children && (
          <Box data-cy="modal-text">
            <Typography variant="body2" component="div">
              <FormattedMessage
                id={`components.modal.${textId}.text`}
                values={{
                  ...values,
                  info: (msg: ReactNode) => (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {msg}
                    </Typography>
                  ),
                }}
              />
            </Typography>
          </Box>
        )}
        {children}
      </DialogContent>
      <DialogActions sx={{ pb: 2, pr: 2 }}>
        <Button
          variant="outlined"
          data-cy="modal-cancel-button"
          color="secondary"
          onClick={() => handleCancel()}
        >
          <FormattedMessage id={`components.modal.${textId}.cancel`} />
        </Button>
        <Button
          autoFocus
          variant="contained"
          data-cy="modal-confirm-button"
          color={confirmType === 'Primary' ? 'primary' : 'error'}
          onClick={() => handleConfirm()}
        >
          <FormattedMessage id={`components.modal.${textId}.confirm`} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  close: () => dispatch(closeModal()),
  dispatch,
});

const mapStateToProps = (
  state: ReduxState,
  { isOpen, textId, onConfirm, values, confirmType }: OwnProps
): StateProps => ({
  confirmAction: state.modal.confirmAction,
  confirmType: confirmType || state.modal.confirmType,
  isOpen: isOpen || state.modal.isOpen,
  onConfirm: onConfirm || state.modal.onConfirm,
  textId: textId || state.modal.textId,
  values: values || state.modal.values,
});

export default connect<StateProps, DispatchProps, OwnProps, ReduxState>(
  mapStateToProps,
  mapDispatchToProps
)(ReduxModal);
